import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from 'config/axiosConfig';
import { Meeting } from 'util/constants';
import { useUser } from 'util/UserContext';
import MeetingCard from 'components/Meetings/MeetingCard';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import { toast } from 'sonner';
import MeetingProfileTabs from './MeetingProfileTabs';
import { CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';
import DialogConfirmation from 'ui/DialogConfirmation';
import NotFoundPage from 'components/ResponsePages/404NotFoundPage';
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage';
import { AxiosError } from 'axios';
import { setDocumentTitle } from 'util/pageTitles';

const MeetingProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentUser, setShowAuthModal } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [editMode, setEditMode] = useState<boolean>(false);
  const [description, setDescription] = useState<string>('');
  const [addedOrganizers, setAddedOrganizers] = useState<CoauthorSuggestion[]>([]);
  const [removedOrganizers, setRemovedOrganizers] = useState<CoauthorSuggestion[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    if (meeting?.title) {
      setDocumentTitle(`Event - ${meeting.title}`);
    }
  }, [meeting?.title]);

  useEffect(() => {
    const fetchMeeting = async () => {
      setIsLoading(true);
      try {
        const response = await api.get<Meeting>(`/api/meetings/${id}`);
        setMeeting(response.data);
        setDescription(response.data.description || '');
        
        if (searchParams.get('edit') === 'true') {
          setSearchParams({});
          
          // Only allow editing if the user is the owner
          if (currentUser?.id === response.data.user_id) {
            setEditMode(true);
          }
        }
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 404) {
          setError('404: Failed to load event profile. Please try again later.');
        } else {
          setError('Failed to load event profile. Please try again later.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchMeeting();
  }, [id, searchParams, currentUser, setSearchParams]);

  const handleOrganizersChange = (added: CoauthorSuggestion[], removed: CoauthorSuggestion[]) => {
    setAddedOrganizers(added);
    setRemovedOrganizers(removed);
  };

  const handleSave = async () => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Update meeting description
      if (description !== meeting?.description) {
        await api.patch(`/api/meetings/${id}`, {
          description
        });
      }

      // Update organizers
      if (addedOrganizers.length > 0 || removedOrganizers.length > 0) {
        await api.post(`/api/meetings/${id}/organizers`, {
          addedOrganizers: addedOrganizers.map(c => ({
            id: c.id,
            type: c.type
          })),
          removedOrganizers: removedOrganizers.map(c => ({
            id: c.id,
            type: c.type
          }))
        });
      }

      // Update local meeting state
      setMeeting(prev => prev ? {
        ...prev,
        description,
      } : null);
      
      // Clear edit mode
      setEditMode(false);
      setAddedOrganizers([]);
      setRemovedOrganizers([]);
      setSearchParams({});
      
      toast.success('Event updated successfully!');
    } catch (error: any) {
      console.error('Error updating meeting:', error);
      toast.error('Error updating event. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setSearchParams({});
    setDescription(meeting?.description || '');
    setAddedOrganizers([]);
    setRemovedOrganizers([]);
  };

  if (error) {
    return error.match(/404/) ? 
      <NotFoundPage 
        displayStyle="page" 
        errorMessage="Sorry, we couldn't find this event." 
        errorSubtext="It may be only visible to specific users. Double check the URL or try reloading the page." 
        buttonText="See Other Events" 
        buttonHref="/events" 
      /> : 
      <ServerErrorPage />;
  }

  if (isLoading || !meeting) {
    return <LoadingSkeleton count={1} height="h-80" className="mt-4" />;
  }

  const editorButtons = editMode ? (
    <div className="flex justify-end gap-2">
      <button
        onClick={handleCancel}
        className="btn-secondary py-1.5"
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        onClick={handleSave}
        className={isSubmitting ? "btn-primary-disabled py-1.5" : "btn-primary py-1.5"}
        disabled={isSubmitting}
      >
        Save
      </button>
    </div>
  ) : null;

  return (
    <div className="py-0 relative">
      <div>
        <MeetingCard 
          meeting={meeting}
          isMeetingProfilePage={true}
          truncateDescription={true}
          editMode={editMode}
          onDescriptionChange={setDescription}
          onOrganizersChange={handleOrganizersChange}
        >
          {editorButtons}
        </MeetingCard>
      </div>
      
      <MeetingProfileTabs meeting={meeting} />
      
      {/* Cancel Confirmation Dialog */}
      {isCancelDialogOpen && (
        <DialogConfirmation
          isOpen={isCancelDialogOpen}
          onCancel={() => setIsCancelDialogOpen(false)}
          onConfirm={handleCancel}
          title="Discard Changes"
          description="You have unsaved changes. Are you sure you want to discard them?"
          confirmText="Discard"
          cancelText="Keep Editing"
        />
      )}
    </div>
  );
};

export default MeetingProfile;
